export interface RoleData {
  id: string;
  title: string;
  category: 'tech' | 'finance' | 'marketing' | 'operations' | 'legal' | 'healthcare' | 'creative' | 'consulting' | 'sales' | 'hr';
  basePremium: number; // 0.15 - 0.56
  baseSalary: number; // median annual salary
  aiExposureTier: 'Low' | 'Medium' | 'High' | 'Very High';
  automationProbability: number; // 0.0 - 0.99
  topSkills: string[];
  repetitiveTaskPercent: number;
  baselineGrowth: number; // annual wage growth %
}

export const roles: RoleData[] = [
  {
    id: 'software-engineer',
    title: 'Software Engineer',
    category: 'tech',
    basePremium: 0.49,
    baseSalary: 127260,
    aiExposureTier: 'Very High',
    automationProbability: 0.31,
    topSkills: ['Machine Learning', 'Prompt Engineering', 'MLOps'],
    repetitiveTaskPercent: 45,
    baselineGrowth: 0.025
  },
  {
    id: 'data-analyst',
    title: 'Data Analyst',
    category: 'tech',
    basePremium: 0.46,
    baseSalary: 73805,
    aiExposureTier: 'Very High',
    automationProbability: 0.44,
    topSkills: ['Python', 'SQL + AI', 'Data Storytelling'],
    repetitiveTaskPercent: 55,
    baselineGrowth: 0.025
  },
  {
    id: 'product-manager',
    title: 'Product Manager',
    category: 'tech',
    basePremium: 0.38,
    baseSalary: 102496,
    aiExposureTier: 'High',
    automationProbability: 0.24,
    topSkills: ['AI Product Strategy', 'Prompt Engineering', 'Data Analytics'],
    repetitiveTaskPercent: 40,
    baselineGrowth: 0.022
  },
  {
    id: 'marketing-manager',
    title: 'Marketing Manager',
    category: 'marketing',
    basePremium: 0.35,
    baseSalary: 78674,
    aiExposureTier: 'High',
    automationProbability: 0.38,
    topSkills: ['Generative AI Content', 'Marketing Automation', 'Data Analytics'],
    repetitiveTaskPercent: 50,
    baselineGrowth: 0.018
  },
  {
    id: 'financial-analyst',
    title: 'Financial Analyst',
    category: 'finance',
    basePremium: 0.42,
    baseSalary: 83500,
    aiExposureTier: 'High',
    automationProbability: 0.55,
    topSkills: ['Python for Finance', 'AI Forecasting', 'Data Visualization'],
    repetitiveTaskPercent: 60,
    baselineGrowth: 0.02
  },
  {
    id: 'accountant',
    title: 'Accountant',
    category: 'finance',
    basePremium: 0.30,
    baseSalary: 65000,
    aiExposureTier: 'High',
    automationProbability: 0.72,
    topSkills: ['AI-Powered Accounting', 'RPA', 'Data Analytics'],
    repetitiveTaskPercent: 75,
    baselineGrowth: 0.015
  },
  {
    id: 'lawyer',
    title: 'Lawyer',
    category: 'legal',
    basePremium: 0.28,
    baseSalary: 127990,
    aiExposureTier: 'Medium',
    automationProbability: 0.18,
    topSkills: ['Legal AI Research', 'Contract Analysis AI', 'Document Automation'],
    repetitiveTaskPercent: 45,
    baselineGrowth: 0.018
  },
  {
    id: 'paralegal',
    title: 'Paralegal',
    category: 'legal',
    basePremium: 0.25,
    baseSalary: 56000,
    aiExposureTier: 'High',
    automationProbability: 0.68,
    topSkills: ['Legal Document Automation', 'eDiscovery AI', 'Contract Review'],
    repetitiveTaskPercent: 80,
    baselineGrowth: 0.015
  },
  {
    id: 'ux-designer',
    title: 'UX Designer',
    category: 'creative',
    basePremium: 0.32,
    baseSalary: 80150,
    aiExposureTier: 'Medium',
    automationProbability: 0.22,
    topSkills: ['AI UX Tools', 'Design Systems', 'User Research Automation'],
    repetitiveTaskPercent: 35,
    baselineGrowth: 0.02
  },
  {
    id: 'content-writer',
    title: 'Content Writer / Copywriter',
    category: 'creative',
    basePremium: 0.36,
    baseSalary: 52000,
    aiExposureTier: 'High',
    automationProbability: 0.58,
    topSkills: ['Generative AI Writing', 'SEO AI Tools', 'Content Strategy'],
    repetitiveTaskPercent: 65,
    baselineGrowth: 0.015
  },
  {
    id: 'operations-manager',
    title: 'Operations Manager',
    category: 'operations',
    basePremium: 0.33,
    baseSalary: 77740,
    aiExposureTier: 'Medium',
    automationProbability: 0.42,
    topSkills: ['Process Automation', 'AI Analytics', 'RPA'],
    repetitiveTaskPercent: 55,
    baselineGrowth: 0.018
  },
  {
    id: 'hr-specialist',
    title: 'HR Specialist',
    category: 'hr',
    basePremium: 0.27,
    baseSalary: 62000,
    aiExposureTier: 'Medium',
    automationProbability: 0.45,
    topSkills: ['HR AI Tools', 'Recruiting Automation', 'People Analytics'],
    repetitiveTaskPercent: 60,
    baselineGrowth: 0.015
  },
  {
    id: 'sales-representative',
    title: 'Sales Representative',
    category: 'sales',
    basePremium: 0.29,
    baseSalary: 68000,
    aiExposureTier: 'Medium',
    automationProbability: 0.35,
    topSkills: ['Sales AI Tools', 'CRM Automation', 'AI Prospecting'],
    repetitiveTaskPercent: 50,
    baselineGrowth: 0.02
  },
  {
    id: 'consultant',
    title: 'Management Consultant',
    category: 'consulting',
    basePremium: 0.40,
    baseSalary: 95000,
    aiExposureTier: 'Medium',
    automationProbability: 0.28,
    topSkills: ['AI Strategy', 'Data Analytics', 'Generative AI Research'],
    repetitiveTaskPercent: 40,
    baselineGrowth: 0.022
  },
  {
    id: 'healthcare-admin',
    title: 'Healthcare Administrator',
    category: 'healthcare',
    basePremium: 0.22,
    baseSalary: 70000,
    aiExposureTier: 'Medium',
    automationProbability: 0.50,
    topSkills: ['Healthcare AI Tools', 'Workflow Automation', 'Data Analytics'],
    repetitiveTaskPercent: 65,
    baselineGrowth: 0.018
  },
  {
    id: 'customer-support',
    title: 'Customer Support Specialist',
    category: 'operations',
    basePremium: 0.26,
    baseSalary: 45000,
    aiExposureTier: 'High',
    automationProbability: 0.65,
    topSkills: ['AI Chatbots', 'Support Automation', 'CRM Analytics'],
    repetitiveTaskPercent: 75,
    baselineGrowth: 0.015
  }
];

export const industries = [
  { id: 'technology', name: 'Technology', modifier: 0.08 },
  { id: 'finance', name: 'Finance & Insurance', modifier: 0.06 },
  { id: 'healthcare', name: 'Healthcare', modifier: 0.02 },
  { id: 'consulting', name: 'Consulting', modifier: 0.05 },
  { id: 'marketing', name: 'Marketing & Media', modifier: 0.04 },
  { id: 'retail', name: 'Retail & E-commerce', modifier: 0.03 },
  { id: 'manufacturing', name: 'Manufacturing', modifier: 0.02 },
  { id: 'education', name: 'Education', modifier: -0.02 },
  { id: 'government', name: 'Government', modifier: -0.03 },
  { id: 'nonprofit', name: 'Nonprofit', modifier: -0.04 }
];

export const experienceLevels = [
  { id: 'entry', name: 'Entry-level (0-2 years)', modifier: -0.05, seniorityLabel: 'entry' },
  { id: 'mid', name: 'Mid-level (3-7 years)', modifier: 0.0, seniorityLabel: 'mid' },
  { id: 'senior', name: 'Senior (8-15 years)', modifier: 0.08, seniorityLabel: 'senior' },
  { id: 'executive', name: 'Executive / Leadership (15+ years)', modifier: 0.12, seniorityLabel: 'senior' }
];

export const aiUsageLevels = [
  { id: 'none', name: 'Not using AI yet', capture: 0.0 },
  { id: 'weekly', name: 'Occasionally (weekly)', capture: 0.25 },
  { id: 'few-times-week', name: 'A few times a week', capture: 0.45 },
  { id: 'daily', name: 'Daily', capture: 0.70 },
  { id: 'power-user', name: 'Power user / integrated', capture: 0.90 }
];

export function getRoleById(id: string): RoleData | undefined {
  return roles.find(r => r.id === id);
}
